import { processTreeViewItems } from '@progress/kendo-react-treeview'
import React from 'react'
import { useSet } from 'react-use'
import * as R from 'remeda'

import {
  StyledTreeView,
  type StyledTreeViewProps,
} from '~/components/elements/StyledTreeView'
import {
  type CustomerAttribute as CustomerAttributeBase,
  type CustomerAttributeGroup as CustomerAttributeGroupBase,
} from '~/gql/graphql'

type CustomerAttribute = App.PickGraphQL<CustomerAttributeBase, 'id' | 'name'>
type CustomerAttributeGroup<TCustomerAttribute extends CustomerAttribute> =
  App.PickGraphQL<CustomerAttributeGroupBase, 'id' | 'name'> & {
    customerAttributes: TCustomerAttribute[]
  }

export type CustomerAttributeTreeViewProps<
  TCustomerAttributeGroup extends CustomerAttributeGroup<TCustomerAttribute>,
  TCustomerAttribute extends CustomerAttribute,
> = {
  customerAttributeGroups: TCustomerAttributeGroup[]
  'data-testid'?: string
  onItemClick?: (customerAttributeIds: TCustomerAttribute['id'][]) => void
}

export const CustomerAttributeTreeView = <
  TCustomerAttributeGroup extends CustomerAttributeGroup<TCustomerAttribute>,
  TCustomerAttribute extends CustomerAttribute,
>({
  customerAttributeGroups,
  'data-testid': dataTestId = 'components-features-customer-attribute-tree-view',
  onItemClick,
}: CustomerAttributeTreeViewProps<
  TCustomerAttributeGroup,
  TCustomerAttribute
>) => {
  const [expandIdSet, { toggle: toggleExpandIdSet }] = useSet<string>(new Set())

  const data = React.useMemo(
    () =>
      processTreeViewItems(customerAttributeGroups, {
        expand: { ids: [...expandIdSet], idField: 'id' },
      }),
    [customerAttributeGroups, expandIdSet],
  )

  const handleExpandChange = React.useCallback<
    EH<StyledTreeViewProps, 'onExpandChange'>
  >(
    (event) => {
      toggleExpandIdSet(event.item.id)
    },
    [toggleExpandIdSet],
  )

  const handleItemClick = React.useCallback<
    EH<StyledTreeViewProps, 'onItemClick'>
  >(
    (event) => {
      const customerAttributes: TCustomerAttribute[] = event.item
        .customerAttributes ?? [event.item]

      onItemClick?.(customerAttributes.map(R.prop('id')))
    },
    [onItemClick],
  )

  return (
    <StyledTreeView
      childrenField="customerAttributes"
      data={data}
      data-testid={dataTestId}
      expandIcons={true}
      textField="name"
      onExpandChange={handleExpandChange}
      onItemClick={handleItemClick}
    />
  )
}
